import React, { useState, useRef, useCallback } from 'react';
import { FileUpload, ProcessingResult } from '../types/voucher';
import { buildApiUrl } from '../config/api';

interface FileUploadComponentProps {
    onUploadSuccess?: (result: ProcessingResult) => void;
    onUploadError?: (error: string) => void;
}

export const FileUploadComponent: React.FC<FileUploadComponentProps> = ({
    onUploadSuccess,
    onUploadError
}) => {
    const [isDragging, setIsDragging] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadHistory, setUploadHistory] = useState<FileUpload[]>([]);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [selectedUploadId, setSelectedUploadId] = useState<number | null>(null);
    const [uploadResult, setUploadResult] = useState<ProcessingResult | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Fetch upload history on component mount
    React.useEffect(() => {
        // Only fetch if we're in a browser environment and server might be running
        if (typeof window !== 'undefined') {
            fetchUploadHistory();
        }
    }, []);

    const fetchUploadHistory = async () => {
        try {
            const response = await fetch(buildApiUrl('/api/upload/history'));

            if (!response.ok) {
                console.error('Server responded with status:', response.status);
                return;
            }

            const text = await response.text();
            if (!text) {
                console.error('Empty response from server');
                return;
            }

            const data = JSON.parse(text);
            if (data.success) {
                setUploadHistory(data.uploads);
            }
        } catch (error) {
            console.error('Failed to fetch upload history:', error);
            // Don't show error to user for history fetch failure
        }
    };

    const handleDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(true);
    }, []);

    const handleDragLeave = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
    }, []);

    const handleDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);

        const files = Array.from(e.dataTransfer.files);
        const excelFile = files.find(file =>
            file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
            file.type === 'application/vnd.ms-excel'
        );

        if (excelFile) {
            setSelectedFile(excelFile);
        } else {
            onUploadError?.('Please select a valid Excel file (.xlsx or .xls)');
        }
    }, [onUploadError]);

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setSelectedFile(file);
        }
    };

    const handleUpload = async () => {
        if (!selectedFile) {
            console.log('No file selected');
            return;
        }

        console.log('Starting upload for file:', selectedFile.name);
        setIsUploading(true);
        setUploadResult(null);

        try {
            const formData = new FormData();
            formData.append('file', selectedFile);

            console.log('Sending request to /api/upload/excel...');
            const response = await fetch(buildApiUrl('/api/upload/excel'), {
                method: 'POST',
                body: formData
            });

            console.log('Response status:', response.status);
            console.log('Response ok:', response.ok);

            // Try to read JSON even on error responses to surface detailed messages
            let result: any = null;
            try {
                const text = await response.text();
                result = text ? JSON.parse(text) : null;
            } catch (_) {
                // ignore JSON parse errors
            }

            if (!response.ok) {
                if (result) {
                    setUploadResult(result);
                    onUploadSuccess?.(result); // allow UI to show details panel
                    onUploadError?.(result.message || result.error || `HTTP ${response.status}`);
                } else {
                    onUploadError?.(`HTTP ${response.status}: ${response.statusText}`);
                }
                return;
            }

            if (!result) {
                result = await response.json();
            }
            console.log('Upload result:', result);

            if (result.success) {
                setUploadResult(result);
                onUploadSuccess?.(result);
                fetchUploadHistory(); // Refresh history
                setSelectedFile(null); // Clear selected file
                if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                }
                console.log('Upload completed successfully');
            } else {
                setUploadResult(result);
                onUploadError?.(result.error || 'Upload failed');
                console.error('Upload failed:', result.error);
            }
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Upload failed';
            console.error('Upload error:', error);
            onUploadError?.(errorMessage);
        } finally {
            setIsUploading(false);
        }
    };

    const formatDate = (date: string | Date) => {
        return new Date(date).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold mb-4">Upload Voucher Data</h2>

            {/* Upload History Dropdown */}
            {uploadHistory.length > 0 && (
                <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Previous Uploads (Last Data Updated)
                    </label>
                    <div className="space-y-2">
                        <select
                            className="w-full p-2 border border-gray-300 rounded-md bg-white"
                            onChange={(e) => setSelectedUploadId(e.target.value ? Number(e.target.value) : null)}
                        >
                            <option value="">Select previous upload to view details...</option>
                            {uploadHistory.map((upload) => (
                                <option key={upload.id} value={upload.id}>
                                    {upload.filename} - Last Updated: {formatDate(upload.lastUpdateDate)}
                                    ({upload.recordsProcessed} records)
                                </option>
                            ))}
                        </select>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={async () => {
                                    if (!selectedUploadId) return;
                                    try {
                                        const res = await fetch(buildApiUrl('/api/upload/reprocess'), {
                                            method: 'POST',
                                            headers: { 'Content-Type': 'application/json' },
                                            body: JSON.stringify({ id: selectedUploadId })
                                        });
                                        const json = await res.json();
                                        if (json.success) {
                                            // Trigger refresh across views
                                            window.dispatchEvent(new CustomEvent('refreshAnalytics'));
                                        } else {
                                            console.error('Reprocess failed:', json.error || json.message);
                                        }
                                    } catch (e) {
                                        console.error('Reprocess error:', e);
                                    }
                                }}
                                disabled={!selectedUploadId}
                                className="px-3 py-2 text-sm bg-gray-100 rounded hover:bg-gray-200 text-gray-800 disabled:opacity-50"
                            >
                                Re-process analytics
                            </button>
                            <button
                                onClick={() => window.dispatchEvent(new CustomEvent('refreshAnalytics'))}
                                className="px-3 py-2 text-sm bg-white border rounded hover:bg-gray-50 text-gray-800"
                            >
                                Refresh now
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* File Upload Area */}
            <div
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${isDragging
                    ? 'border-blue-400 bg-blue-50'
                    : 'border-gray-300 hover:border-gray-400'
                    }`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
            >
                <div className="space-y-4">
                    <div className="mx-auto w-12 h-12 text-gray-400">
                        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                            />
                        </svg>
                    </div>

                    {selectedFile ? (
                        <div>
                            <p className="text-sm text-gray-600">Selected file:</p>
                            <p className="font-medium">{selectedFile.name}</p>
                            <p className="text-xs text-gray-500">
                                {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                            </p>
                        </div>
                    ) : (
                        <div>
                            <p className="text-lg font-medium text-gray-900">
                                Drop your Excel file here
                            </p>
                            <p className="text-sm text-gray-500">
                                or click to browse (.xlsx, .xls files only)
                            </p>
                        </div>
                    )}

                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".xlsx,.xls"
                        onChange={handleFileSelect}
                        className="hidden"
                    />

                    <button
                        onClick={() => fileInputRef.current?.click()}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                        disabled={isUploading}
                    >
                        {selectedFile ? 'Change File' : 'Browse Files'}
                    </button>
                </div>
            </div>

            {/* Upload Button */}
            {selectedFile && (
                <div className="mt-4 flex justify-end">
                    <button
                        onClick={handleUpload}
                        disabled={isUploading}
                        className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-400 transition-colors flex items-center space-x-2"
                    >
                        {isUploading && (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        )}
                        <span>{isUploading ? 'Processing...' : 'Upload & Process'}</span>
                    </button>
                </div>
            )}

            {/* Upload Result */}
            {uploadResult && (
                <div className={`mt-4 p-4 rounded-md ${uploadResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
                    }`}>
                    <div className="flex items-start">
                        <div className={`flex-shrink-0 ${uploadResult.success ? 'text-green-400' : 'text-red-400'
                            }`}>
                            {uploadResult.success ? (
                                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                            ) : (
                                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                </svg>
                            )}
                        </div>
                        <div className="ml-3">
                            <h3 className={`text-sm font-medium ${uploadResult.success ? 'text-green-800' : 'text-red-800'
                                }`}>
                                {uploadResult.success ? 'Upload Successful' : 'Upload Failed'}
                            </h3>
                            <div className={`mt-2 text-sm ${uploadResult.success ? 'text-green-700' : 'text-red-700'
                                }`}>
                                <p>{uploadResult.message}</p>
                                {uploadResult.recordsProcessed > 0 && (
                                    <p>Records processed: {uploadResult.recordsProcessed}</p>
                                )}
                                {uploadResult.lastUpdateDate && (
                                    <p>Last data updated: {formatDate(uploadResult.lastUpdateDate)}</p>
                                )}
                                {uploadResult.errors && uploadResult.errors.length > 0 && (
                                    <div className="mt-2">
                                        <p className="font-medium">Errors:</p>
                                        <ul className="list-disc list-inside">
                                            {uploadResult.errors.slice(0, 5).map((error, index) => (
                                                <li key={index} className="text-xs">
                                                    {error.line && `Line ${error.line}: `}{error.message}
                                                </li>
                                            ))}
                                            {uploadResult.errors.length > 5 && (
                                                <li className="text-xs">... and {uploadResult.errors.length - 5} more errors</li>
                                            )}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* File Format Help */}
            <div className="mt-6 text-sm text-gray-600">
                <h4 className="font-medium mb-2">Expected File Format:</h4>
                <ul className="list-disc list-inside space-y-1">
                    <li>Excel files (.xlsx or .xls)</li>
                    <li>First row: "Last Update: MM/DD/YYYY"</li>
                    <li>Sheets: CLF (Foundational) or DVA (Associate)</li>
                    <li>Required: Voucher Number, Product Name, Assignee Email</li>
                    <li>Max size: 10MB</li>
                </ul>
            </div>
        </div>
    );
};